import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CloseRounded as CloseRoundedIcon,
  TuneOutlined as TuneIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import PageTitleIcon from "../PageTitleIcon";
import { APP_FONT_FAMILY } from "../../constants/fonts";
import {
  getReportSectionsForOrgType,
  type ReportPdfSectionId,
} from "../../constants/reportCustomize.catalog";
import type { OrganizationType } from "../../types/organization.type";

interface ReportCustomizeDialogProps {
  open: boolean;
  orgType: OrganizationType;
  selectedSections: ReportPdfSectionId[];
  onClose: () => void;
  onSave: (selectedSections: ReportPdfSectionId[]) => void;
}

const ReportCustomizeDialog: React.FC<ReportCustomizeDialogProps> = ({
  open,
  orgType,
  selectedSections,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const availableSections = useMemo(
    () => getReportSectionsForOrgType(orgType),
    [orgType],
  );
  const [draft, setDraft] = useState<ReportPdfSectionId[]>(selectedSections);

  useEffect(() => {
    if (open) setDraft(selectedSections);
  }, [open, selectedSections]);

  const selectAll = availableSections.length > 0 && draft.length === availableSections.length;

  const toggleSection = (id: ReportPdfSectionId) => {
    setDraft((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setDraft(checked ? [...availableSections] : []);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.28 : 0.12)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 24px 64px ${alpha("#000", 0.55)}`
              : `0 20px 50px ${alpha("#0f172a", 0.16)}`,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          position: "relative",
          px: { xs: 2.5, sm: 3 },
          pt: 2.75,
          pb: 2.25,
          fontSize: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
          letterSpacing: "inherit",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 55%, transparent 100%)`
              : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 55%, ${theme.palette.background.paper} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.1)}`,
        }}
      >
        <IconButton
          aria-label={t("common.close")}
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            insetInlineEnd: 12,
            color: "text.secondary",
            bgcolor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.2 : 0.04),
            "&:hover": {
              bgcolor: alpha(
                theme.palette.common.black,
                theme.palette.mode === "dark" ? 0.32 : 0.08,
              ),
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 5 }}>
          <PageTitleIcon icon={<TuneIcon />} size={48} />
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: APP_FONT_FAMILY,
                fontWeight: 800,
                fontSize: { xs: "1.2rem", sm: "1.35rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
                color: "text.primary",
              }}
            >
              {t("report.customize")}
            </Typography>
            <Typography
              component="p"
              sx={{
                fontFamily: APP_FONT_FAMILY,
                mt: 0.5,
                fontWeight: 500,
                fontSize: "0.875rem",
                lineHeight: 1.45,
                color: "text.secondary",
              }}
            >
              {t("report.customizeSubtitle")}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          maxHeight: "55vh",
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "10px",
            backgroundColor: theme.palette.action.hover,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>{t("report.selectAllSections")}</Typography>
          <Checkbox
            checked={selectAll}
            indeterminate={draft.length > 0 && !selectAll}
            onChange={(event) => handleSelectAll(event.target.checked)}
            inputProps={{ "aria-label": t("report.selectAllSections") }}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {availableSections.map((id) => {
            const checked = draft.includes(id);
            return (
              <Box
                key={id}
                onClick={() => toggleSection(id)}
                sx={{
                  p: 1.75,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  opacity: checked ? 1 : 0.85,
                  boxShadow: checked ? `0 0 0 2px ${theme.palette.primary.main}22` : "none",
                  transition: "all 0.18s ease",
                  "&:hover": {
                    opacity: 1,
                    boxShadow: `0 4px 12px ${theme.palette.action.hover}`,
                  },
                }}
              >
                <Checkbox
                  checked={checked}
                  sx={{
                    p: 0,
                    "&.Mui-checked": { color: theme.palette.primary.main },
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, userSelect: "none" }}>
                    {t(`report.sections.${id}`)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", userSelect: "none" }}
                  >
                    {t(`report.sectionHints.${id}`)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(
            theme.palette.primary.main,
            theme.palette.mode === "dark" ? 0.06 : 0.03,
          ),
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={() => onSave(draft)}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            px: 2.5,
            boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          {t("report.saveChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportCustomizeDialog;
